import React, { useCallback, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { Table } from '../../../../../../../components/Table'
import { TableBody } from '../../../../../../../components/Table/TableBody'
import { TableHead } from '../../../../../../../components/Table/TableHead'
import { createProvider, deleteCredentialRule } from '../../core/api'
import { TRAFFIC_TYPES } from '../../core/consts'
import { IProviderCreateBody } from '../../core/requests.types'
import { IProviderBase, IProviderCredentialRule, IProviderDetails, IProviderFormMeta } from '../../core/types'
import ModalProviderCredentialRule from './ModalProviderCredentialRule'

interface Props {
  show: boolean
  onClose: (open: boolean) => void
  onSaved: (provider: IProviderBase, details?: IProviderDetails) => void
  onBindingsChanged?: (details?: IProviderDetails) => void
  formMeta: IProviderFormMeta | null
  initialProvider?: IProviderBase | null
  initialDetails?: IProviderDetails | null
  canUpdateKeys?: boolean
}

const formatKeysSet = (keysState: IProviderCredentialRule['keysState']): string => {
  const set: string[] = []
  if (keysState.apiKey) set.push('API')
  if (keysState.publicKey) set.push('Public')
  if (keysState.privateKey) set.push('Private')
  if (keysState.privateKeyPassphrase) set.push('Passphrase')
  return set.length > 0 ? set.join(', ') : '—'
}

const formatAmountRange = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return '—'
  if (min !== null && max !== null) return `${min} — ${max}`
  if (min !== null) return `от ${min}`
  return `до ${max}`
}

const formatCurrencies = (currencies: string[] | null): string =>
  currencies && currencies.length > 0 ? currencies.join(', ') : '—'

const formatTrafficTypes = (trafficTypes: string[]): string => {
  if (!trafficTypes || trafficTypes.length === 0) return '—'
  return trafficTypes.map((tt) => TRAFFIC_TYPES.find((t) => t.value === tt)?.label ?? tt).join(', ')
}

export default ({ show, onClose, onSaved, onBindingsChanged, formMeta, initialProvider, initialDetails, canUpdateKeys = false }: Props) => {
  const isEdit = !!initialProvider
  const [providerName, setProviderName] = useState(initialProvider?.name ?? '')
  const [enabled, setEnabled] = useState(initialProvider?.enabled ?? true)
  const [credentialsRequired, setCredentialsRequired] = useState(initialProvider?.credentialsRequired ?? true)
  const [externalGateway, setExternalGateway] = useState(initialProvider?.externalGateway ?? false)
  const [isSaving, setIsSaving] = useState(false)
  const [details, setDetails] = useState<IProviderDetails | null>(initialDetails ?? null)
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<IProviderCredentialRule | null>(null)

  React.useEffect(() => {
    if (!show) return
    setProviderName(initialProvider?.name ?? '')
    setEnabled(initialProvider?.enabled ?? true)
    setCredentialsRequired(initialProvider?.credentialsRequired ?? true)
    setExternalGateway(initialProvider?.externalGateway ?? false)
    setEditingRule(null)
    setIsRuleModalOpen(false)
  }, [show, initialProvider])

  // Kept apart from the reset above: the parent refreshes its details cache after every binding
  // change, so this runs while the modal is open and must not close the rule form under the user.
  React.useEffect(() => {
    if (!show) return
    setDetails(initialDetails ?? null)
  }, [show, initialDetails])

  const isFormComplete = useMemo(() => providerName.trim().length > 1, [providerName])

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      const response = await createProvider({
        name: providerName.trim(),
        enabled,
        credentialsRequired,
        externalGateway,
      } as IProviderCreateBody)

      if (!response?.data?.success) {
        throw new Error((response?.data as any)?.errors?.general_error || 'Unknown error')
      }

      const provider = response.data.data
      onSaved(provider)
      onClose(false)
    } catch (error: any) {
      console.log(error)
      Swal.fire('Ошибка', error?.response?.data?.errors?.general_error || error?.data?.errors?.general_error || error?.message || 'Не удалось создать провайдера', 'error')
    } finally {
      setIsSaving(false)
    }
  }, [enabled, credentialsRequired, externalGateway, providerName, onSaved, onClose])

  const handleRuleSaved = useCallback((rule: IProviderCredentialRule) => {
    const provider = details?.provider || initialProvider
    const currentRules = details?.credentialRules ?? []
    const exists = currentRules.some((item) => item.id === rule.id)
    const credentialRules = exists
      ? currentRules.map((item) => (item.id === rule.id ? rule : item))
      : [...currentRules, rule]

    const nextDetails = provider ? { provider, credentialRules } : null
    if (nextDetails) setDetails(nextDetails)
    onBindingsChanged?.(nextDetails ?? undefined)
  }, [details, initialProvider, onBindingsChanged])

  const handleDeleteRule = useCallback(async (ruleId: number) => {
    if (!initialProvider) return
    const confirm = await Swal.fire({
      title: 'Удалить credential-связку?',
      text: 'Удаление нельзя отменить',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Удалить',
      cancelButtonText: 'Отмена',
    })

    if (!confirm.isConfirmed) return

    const response = await deleteCredentialRule(initialProvider.id, ruleId)
    if (!response?.data?.success) {
      Swal.fire('Ошибка', 'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error', 'error')
      return
    }

    const nextDetails = details
      ? { ...details, credentialRules: details.credentialRules.filter((rule) => rule.id !== ruleId) }
      : null
    if (nextDetails) setDetails(nextDetails)
    onBindingsChanged?.(nextDetails ?? undefined)
  }, [details, initialProvider, onBindingsChanged])

  return (
    <>
      <Modal show={show} onHide={() => !isSaving && onClose(false)} centered size='xl'>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? `Связки провайдера: ${initialProvider?.name}` : 'Новый провайдер'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!isEdit && (
            <div className='row mb-8 g-5'>
              <div className='col-md-12'>
                <label className='fs-6 fw-semibold mb-2 required'>Имя провайдера</label>
                <input
                  className='form-control form-control-solid'
                  value={providerName}
                  onChange={(e) => setProviderName(e.target.value)}
                  placeholder='Например: fenixpay'
                />
              </div>
              <div className='col-md-4'>
                <label className='fs-6 fw-semibold mb-2'>Статус</label>
                <div className='form-check form-switch form-check-custom form-check-solid mt-2'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='createEnabled'
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  <label className='form-check-label ms-3' htmlFor='createEnabled'>
                    {enabled ? 'Включён' : 'Выключен'}
                  </label>
                </div>
              </div>
              <div className='col-md-4'>
                <label className='fs-6 fw-semibold mb-2'>Обязательность кредов</label>
                <div className='form-check form-switch form-check-custom form-check-solid mt-2'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='createCredReq'
                    checked={credentialsRequired}
                    onChange={(e) => setCredentialsRequired(e.target.checked)}
                  />
                  <label className='form-check-label ms-3' htmlFor='createCredReq'>
                    {credentialsRequired ? 'Обязательны' : 'Не обязательны'}
                  </label>
                </div>
              </div>
              <div className='col-md-4'>
                <label className='fs-6 fw-semibold mb-2'>Внешняя платёжная страница</label>
                <div className='form-check form-switch form-check-custom form-check-solid mt-2'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='createExtGateway'
                    checked={externalGateway}
                    onChange={(e) => setExternalGateway(e.target.checked)}
                  />
                  <label className='form-check-label ms-3' htmlFor='createExtGateway'>
                    {externalGateway ? 'Включена' : 'Выключена'}
                  </label>
                </div>
              </div>
            </div>
          )}

          {isEdit && (
            <>
              <div className='d-flex justify-content-between align-items-center mb-5'>
                <div>
                  <div className='fw-bold fs-4'>Credential-связки</div>
                  <div className='text-muted fs-7'>
                    Управление merchant / направление / платежный метод и установленными ключами.
                  </div>
                </div>
                <button
                  className='btn btn-sm btn-primary'
                  onClick={() => { setEditingRule(null); setIsRuleModalOpen(true) }}
                >
                  Добавить связку
                </button>
              </div>

              <div className='card border'>
                <div className='card-body py-3' style={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <tr>
                        <th className='min-w-120px'>Мерчанты</th>
                        <th className='min-w-100px'>Направления</th>
                        <th className='min-w-120px'>Тип трафика</th>
                        <th className='min-w-150px'>Платежные методы</th>
                        <th className='min-w-120px'>Внешний ID мерч.</th>
                        <th className='min-w-100px'>Валюты</th>
                        <th className='min-w-140px'>Диапазон сумм</th>
                        <th className='min-w-120px'>Ключи</th>
                        <th className='min-w-80px text-end'></th>
                      </tr>
                    </TableHead>
                    <TableBody>
                      {details?.credentialRules?.length ? (
                        details.credentialRules.map((rule) => (
                          <tr key={rule.id}>
                            <td>{(rule.merchants ?? []).map((m) => m.name).join(', ')}</td>
                            <td>{(rule.directions ?? []).join(', ')}</td>
                            <td>{formatTrafficTypes(rule.trafficTypes)}</td>
                            <td>{(rule.paymentMethods ?? []).map((m) => m.name).join(', ')}</td>
                            <td>{rule.externalMerchantId ?? '—'}</td>
                            <td>{formatCurrencies(rule.currencies)}</td>
                            <td>{formatAmountRange(rule.minAmount, rule.maxAmount)}</td>
                            <td>
                              <span className='text-muted fs-7'>{formatKeysSet(rule.keysState)}</span>
                            </td>
                            <td className='text-end text-nowrap'>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1'
                                title='Редактировать'
                                onClick={() => { setEditingRule(rule); setIsRuleModalOpen(true) }}
                              >
                                <KTIcon iconName='pencil' className='fs-4' />
                              </button>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-bg-light btn-active-color-danger'
                                title='Удалить'
                                onClick={() => handleDeleteRule(rule.id)}
                              >
                                <KTIcon iconName='trash' className='fs-4' />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className='text-center text-muted py-10'>
                            Нет credential-связок
                          </td>
                        </tr>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant='secondary' onClick={() => onClose(false)} disabled={isSaving}>
            {isEdit ? 'Закрыть' : 'Отменить'}
          </Button>
          {!isEdit && (
            <Button variant='primary' onClick={handleSave} disabled={!isFormComplete || isSaving}>
              {isSaving ? 'Сохранение...' : 'Создать'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {isEdit && initialProvider && (
        <ModalProviderCredentialRule
          show={isRuleModalOpen}
          providerId={initialProvider.id}
          formMeta={formMeta}
          rule={editingRule}
          existingRules={details?.credentialRules ?? []}
          canUpdateKeys={canUpdateKeys}
          onClose={setIsRuleModalOpen}
          onSaved={handleRuleSaved}
        />
      )}
    </>
  )
}
